import type { Finding, FindingSeverity } from '@services/auditService';
import type { TestResultItem } from '@services/testService';

/**
 * REV-720: adapt an unregistered scan's Mongo results into finding rows.
 *
 * A scan with no tracked place persists no `FindingInstance` at all —
 * `testExecutionEngine` skips them when `contextId` is absent, because the
 * durable evidence corpus is a platform invariant and every row in it carries
 * a `context_id`. So `/findings?scan_run_id=` cannot answer for such a run, by
 * design rather than by omission, and its results live only in the Mongo
 * `TestResult` under a 24-hour retention.
 *
 * That is why this exists. The alternative — keeping the second table for the
 * unregistered case — is the thing REV-720 is about: two implementations of
 * one list, drifting. One table, two sources, one shape.
 *
 * What these rows deliberately do NOT have:
 *
 *   - a `raw_id` that means anything. There is no FindingInstance to act on,
 *     so the caller passes no `selection` and no `hrefFor`, and the table
 *     renders no checkbox and no link. The id here exists only as a React key.
 *   - a status. Nothing can disposition a finding that was never persisted.
 */
const SEVERITY_RANK: Record<FindingSeverity, number> = {
  critical: 0,
  major: 1,
  minor: 2,
  info: 3,
};

export interface ScanRunFindings {
  rows: Finding[];
  /**
   * The engine's per-instance message, keyed by row id.
   *
   * The Mongo document carries it and the MySQL `FindingInstance` has no column
   * for it, so it is handed to the table as `descriptionFor` rather than hung
   * on the row. Inventing a field the API never sends is the REV-345 class of
   * bug — a declared type and a real response that stop agreeing, silently. The
   * asymmetry between the two sources is visible here instead of hidden in a
   * type.
   *
   * It is built in the same pass as the rows on purpose. Computing these ids
   * twice, in two functions, is precisely the seam this codebase has been bitten
   * by: two sides deriving the same key independently until one of them changes.
   */
  messages: Map<string, string>;
}

export function findingsFromScanResults(results: TestResultItem[] | null): ScanRunFindings {
  const rows: Finding[] = [];
  const messages = new Map<string, string>();

  for (const result of results ?? []) {
    const issues = result.issues ?? [];
    issues.forEach((issue, i) => {
      const severity = (issue.severity ?? 'info') as FindingSeverity;
      // Unique per issue: the same rule failing twice on one page is normal,
      // and a colliding React key silently drops one of the two rows.
      const id = `${result.targetUrl}#${i}-${issue.ruleId}`;
      if (issue.message) {
        messages.set(id, issue.message);
      }
      rows.push({
        id,
        source: 'auto',
        severity,
        rule_id: issue.ruleId,
        // No rule name in the Mongo document — the FindingDefinition catalog is
        // what carries names, and an unregistered run never reaches it. The
        // table falls back to rule_id, which is what it did before.
        rule_name: null,
        wcag_criteria: [],
        status: 'open',
        element_selector: issue.element?.selector ?? null,
        element_html: issue.element?.html ?? null,
        occurrence_count: 1,
        target_url: result.targetUrl,
        raw_id: '',
      });
    });
  }

  // Mirror FindingsService's own ordering so a registered and an unregistered
  // run read the same way down the page: severity first, then rule id. Page is
  // the last tiebreak rather than the first — someone scanning this table is
  // looking for what is worst, not for where it is.
  rows.sort((a, b) => {
    const sa = SEVERITY_RANK[a.severity] ?? 9;
    const sb = SEVERITY_RANK[b.severity] ?? 9;
    if (sa !== sb) return sa - sb;
    const ra = String(a.rule_id || '');
    const rb = String(b.rule_id || '');
    if (ra !== rb) return ra.localeCompare(rb);
    return String(a.target_url || '').localeCompare(String(b.target_url || ''));
  });

  return { rows, messages };
}

/**
 * REV-712: sort and filter an unregistered run's rows in place.
 *
 * A registered run asks the API to sort (GET /findings?sort=&direction=), so
 * this mirrors the API's comparator (findingsService.findingComparator in
 * revenant-api) rather than inventing its own: the same table should order
 * the same way whichever source fed it. `severity` falls back to the order
 * above; `rule` orders by the name the table shows, then severity.
 */
export function sortAndFilterFindingRows(
  rows: Finding[],
  {
    by = 'severity',
    direction = 'asc',
    severity,
    ruleId,
  }: {
    by?: 'severity' | 'rule';
    direction?: 'asc' | 'desc';
    severity?: string;
    ruleId?: string;
  } = {}
): Finding[] {
  const flip = direction === 'desc' ? -1 : 1;
  const bySeverity = (a: Finding, b: Finding) =>
    (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);
  const byName = (a: Finding, b: Finding) =>
    String(a.rule_name || a.rule_id || '').localeCompare(String(b.rule_name || b.rule_id || ''));
  const byRuleId = (a: Finding, b: Finding) =>
    String(a.rule_id || '').localeCompare(String(b.rule_id || ''));
  const byPage = (a: Finding, b: Finding) =>
    String(a.target_url || '').localeCompare(String(b.target_url || ''));
  const compare =
    by === 'rule'
      ? (a: Finding, b: Finding) => flip * byName(a, b) || bySeverity(a, b) || byPage(a, b)
      : (a: Finding, b: Finding) => flip * bySeverity(a, b) || byRuleId(a, b) || byPage(a, b);
  return rows
    .filter((f) => (!severity || f.severity === severity) && (!ruleId || f.rule_id === ruleId))
    .sort(compare);
}
